import type { FC } from 'react'

import {
	Checkbox,
	CheckboxButtonGroup,
	COLORS,
	ConsentText,
	FlowContent,
	FlowFooter,
	FlowPage,
	FlowWrapper,
	ICON_SIZE,
	PrimaryButton,
	SPACING,
	TYPOGRAPHY,
	useCheckboxContext,
} from '@monthio/design-system'
import i18next from 'i18next'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import type { ConsentDto } from '../../types/authenticateSmartcheckSessionDto'

import { TextWithLinks } from '../../components/TextWithLinks/TextWithLinks'
import { useClientConsentModule } from './useClientConsentModule'

interface TranslatedConsent {
	header: string
	id: string
	isOptional: boolean
	requiredText: string
	text: string
}

const ClientConsentText = styled(ConsentText)`
	padding-left: calc(${ICON_SIZE.l} + ${SPACING.m});
`

const ClientConsentItem = styled.div`
	padding-bottom: ${SPACING.l};
	margin-bottom: ${SPACING.l};

	&:not(:last-child) {
		border-bottom: 1px solid ${COLORS.graphics.stroke.secondary};
	}
`

const ClientConsentHeader = styled.h2`
	font: ${TYPOGRAPHY.h2};
	margin: 0;
`

const RequiredInputText = styled.span`
	${TYPOGRAPHY.bodySStrong};
	color: ${COLORS.primitives.errorRed._700};
`

interface ConsentItemProps {
	header: string
	id: string
	isOptional: boolean
	requiredText: string
	text: string
}

const ConsentItem: FC<ConsentItemProps> = ({
	header,
	id,
	isOptional,
	requiredText,
	text,
}) => {
	const { state } = useCheckboxContext()
	const isSelected = state.value.includes(id)

	const handleCheckboxChange = useCallback(() => {
		if (isSelected) {
			state.removeValue(id)
		} else {
			state.addValue(id)
		}
	}, [id, isSelected, state])

	const headerId = `consent-header-${id}`

	return (
		<ClientConsentItem>
			<Checkbox
				aria-label={header}
				isRequired={!isOptional}
				isSelected={isSelected}
				onChange={handleCheckboxChange}
				value={id}
			>
				<ClientConsentHeader id={headerId}>{header}</ClientConsentHeader>
				{!isOptional ? (
					<RequiredInputText aria-hidden="true">
						{requiredText}
					</RequiredInputText>
				) : null}
			</Checkbox>
			<ClientConsentText id={`consent-text-${id}`}>
				<TextWithLinks rawText={text} />
			</ClientConsentText>
		</ClientConsentItem>
	)
}

const areRequiredConsentsAccepted = (
	selectedValues: readonly string[],
	consents: TranslatedConsent[],
) => {
	for (const consent of consents) {
		if (!consent.isOptional && !selectedValues.includes(consent.id)) {
			return false
		}
	}
	return true
}

const mapConsentToTranslatedConsent = (
	language: string,
	consent: ConsentDto,
): TranslatedConsent & { requiredText: string } => {
	const consentTranslation =
		consent.consentTranslations.find((x) => x.languageCode === language) ||
		consent.consentTranslations[0]! // Use any language if translation in given language is missing

	return {
		id: consent.id,
		header: consentTranslation.header,
		text: consentTranslation.text,
		isOptional: consent.isOptional,
		requiredText: i18next.t('required'),
	}
}

export const ClientConsent: FC = () => {
	const {
		config,
		finish,
		data: { consents },
	} = useClientConsentModule()
	const { clientName } = config
	const { t, i18n } = useTranslation()
	const translatedConsent = useMemo(() => {
		return consents.map((consent) =>
			mapConsentToTranslatedConsent(i18n.language, consent),
		)
	}, [i18n.language, consents])

	const [selectedValues, setSelectedValues] = useState<string[]>([])

	const handleSelectionChange = useCallback((selected: string[]) => {
		setSelectedValues(selected)
	}, [])

	const handleContinue = useCallback(() => {
		const consentResponses = translatedConsent.map((consent) => ({
			id: consent.id,
			isAccepted: selectedValues.includes(consent.id),
			isOptional: consent.isOptional,
		}))
		finish(consentResponses)
	}, [translatedConsent, finish, selectedValues])

	const isButtonDisabled = !areRequiredConsentsAccepted(
		selectedValues,
		translatedConsent,
	)

	return (
		<FlowWrapper>
			<FlowContent $spacing="m">
				<FlowPage.Header id="consent-title" title={`${clientName} consent`} />
				<CheckboxButtonGroup
					aria-labelledby="consent-title"
					onChange={handleSelectionChange}
					value={selectedValues}
				>
					{translatedConsent.map((consent) => (
						<ConsentItem
							header={consent.header}
							id={consent.id}
							isOptional={consent.isOptional}
							key={consent.id}
							requiredText={consent.requiredText}
							text={consent.text}
						/>
					))}
				</CheckboxButtonGroup>
			</FlowContent>
			<FlowFooter
				actions={
					<PrimaryButton
						aria-label={
							isButtonDisabled
								? t('clientConsent-continueBtn-noConsent-ariaLabel')
								: t('clientConsent-continueBtn-ariaLabel')
						}
						disabled={isButtonDisabled}
						onClick={handleContinue}
					>
						{t('btn-next')}
					</PrimaryButton>
				}
			/>
		</FlowWrapper>
	)
}
